import { AnyError } from '@microsoft/portal-app/lib/IAnyError';
import { QueryStatus, useQuery } from 'react-query';
import {
  IDirSyncStatusAndLanguagePreference,
  getDirSyncStatusAndLanguagePreferenceRequest,
} from '../../redux/modules/graphApis';
import { QueryKey } from '../models/QueryKey';
import { useAuth } from '@iamexperiences/feature-auth';
import { useReportAPITelemetry } from './useReportAPITelemetry';
import { HttpRequestError } from '../../shared/HttpRequestError';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

interface IQueryResult {
  status: QueryStatus;
  onPremisesSyncEnabled: boolean | null;
  userPrincipalName: string;
  preferredLanguage: string | null;
}

const defaultQueryResult: IQueryResult = {
  status: 'idle',
  onPremisesSyncEnabled: null,
  userPrincipalName: '',
  preferredLanguage: null,
};

export const useGetLanguagePreferenceQuery = (): IQueryResult => {
  const { auth } = useAuth();
  const { startTelemetryReport, stopTelemetryReport } = useReportAPITelemetry('getLanguagePreference');

  const { data, status } = useQuery<IDirSyncStatusAndLanguagePreference, AnyError>(
    QueryKey.SYNC_STATUS_AND_LANGUAGE_PREFERENCE,
    async () => {
      const url = getDirSyncStatusAndLanguagePreferenceRequest().url || '';
      startTelemetryReport({});
      const token = await auth.acquireToken({
        scopes: [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll).scope],
      });

      const response = await fetch(url, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (!response.ok) {
        const error = new HttpRequestError(response.status, response.statusText);
        stopTelemetryReport('error', error);
        throw error;
      }

      const data = (await response.json()) as IDirSyncStatusAndLanguagePreference;

      const { onPremisesSyncEnabled, preferredLanguage } = data;

      const payload = {
        isDirSync: !!onPremisesSyncEnabled,
        displayLanguage: preferredLanguage !== null ? preferredLanguage : '',
      };

      stopTelemetryReport('success', payload);
      return data;
    },
  );

  if (!data) {
    return defaultQueryResult;
  }

  return {
    ...data,
    status,
  };
};
